import React, { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Modal } from '../../templates/Modal';
import { usePrintComponentStore } from './store/usePrintComponentStore';
import { Button } from '../../atoms/Button';
import { Typography } from '../../molecules/Typography';
import { useParams } from 'react-router-dom';
import { useExercisePrintRequest } from './hook/useGetExercises';
import PrintDisplayOptions from './components/PrintDisplayOptions';
import AnswerSheet from '../../templates/PrintPaper/feedback';
import AditionalFilters from './components/AditionalFilters/index';
import './style.css'
import { usePrintComponentRequestStore } from './store/usePrintComponentRequest';
import { Alert } from '../../atoms/Alert';

interface IPrintComponent {
  type: 'exercise' | 'paper' | 'practice' | 'graph';
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
}

const NewPrintComponent: React.FC<IPrintComponent> = ({ type, children, isOpen, onClose, title, subtitle }) => {
  const componentRef = useRef(null);
  const { module } = useParams();
  const { hasApiCall, hasAnswerSheet, data, setData } = usePrintComponentStore();
  const { resetExercisePrint } = usePrintComponentRequestStore();
  const { mutate, isPending, isError } = useExercisePrintRequest();

  const [show, setShow] = useState<boolean>(false);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@page {
        margin: 0;
        margin-top: 16px;
        margin-bottom: 8px;
    }
    @media print {
        body {
            /* Tell browsers to print background colors */
            -webkit-print-color-adjust: exact; /* Chrome/Safari/Edge/Opera */
            color-adjust: exact; /* Firefox */
        }
    }`,
  });

  const handleSubmit = () => {
    mutate({ subarea: module ?? "" })
  }

  const clearStep = () => {
    setShow(true);
    setData([]);
    resetExercisePrint();
  }

  const setButtonGroup = () => {
    if(show) {
      return (
        <div className='w-2/3 m-auto'>
          <Button
            onClick={() => handleSubmit()}
            full
            loading={isPending}
          >
            <Typography
              size='default'
              className='text-white xs:text-sm md:text-md'
            >
              Continuar
            </Typography>
          </Button>
        </div>
      )
    }

    if(type === 'paper') {
      return (
        <div className='w-2/3 m-auto'>
          <Button
            onClick={handlePrint}
            full
            loading={isPending}
          >
            <Typography
              size='default'
              className='text-white xs:text-sm md:text-md'
            >
              Imprimir redação
            </Typography>
          </Button>
        </div>
      )
    }

    return (
      <div className='flex w-full flex-row items-center justify-between gap-xl'>
        {type !== 'graph' && (
          <Fragment>
            <div className='flex-1'>
              <Button
                LeftIcon='chevron_left'
                iconType='material'
                variant='outlined'
                onClick={() => hasApiCall ? clearStep() : onCloseGeneral()}
                full
              >
                <Typography size='default' className='xs:text-sm md:text-md'>
                  Voltar
                </Typography>
              </Button>
            </div>
            <div className='flex-1'>
              <Typography className='text-center'>
                {data && data.length} Exercícios
              </Typography>
            </div>
          </Fragment>
        )}
        <div className='flex-1'>
          <Button
            LeftIcon='print'
            iconType='material'
            onClick={handlePrint}
            full
          >
            <Typography
              size='default'
              className='text-white xs:text-sm md:text-md'
            >
              Imprimir lista
            </Typography>
          </Button>
        </div>
      </div>
    )
  }

  const onCloseGeneral = () => {
    onClose();
  }

  useEffect(() => {
    if(isError) {
      console.log('Error');
    }
  }, [isPending])

  useEffect(() => {
    if(hasApiCall) {
      setData([]);
      setShow(true)
    }
  }, [hasApiCall])

  useEffect(() => {
    if(hasApiCall && data.length > 0) {
      setShow(false)
    }
  }, [data])

  return (
    <Modal
      position="top"
      size='md'
      isOpen={isOpen}
      onClose={() => onCloseGeneral()}
      className='xs:w-[90%] md:w-[70%] lg:w-[60%]'
      title={title}
      subtitle={subtitle}
    >
      <div className='flex flex-col gap-xl bg-color-lightest'>
        {type === 'practice' && <Alert
          message={
            <Typography size='default' className='text-card-color-negative'>
              Para imprimir com o gabarito, finalize o simulado.
            </Typography>
          }
          variant='warning'
        />}
        {hasApiCall && <AditionalFilters disabled={data?.length > 0} subject={title ?? "-"}/>}
        {type === 'exercise' && <PrintDisplayOptions />}
        {!show && (
          <div id="paperContainer" className='bg-color-lighter h-[60vh] overflow-auto rounded-lg'>
            <div ref={componentRef} className="w-full h-fit">
              {children}
            </div>
          </div>
        )}
        {hasAnswerSheet && !show && <AnswerSheet /> }
        {setButtonGroup()}
      </div>
    </Modal>
  );
}

export default NewPrintComponent;
